import { API_BASE_URL } from '@synergy/config';
import { AuctionIcon, PrintIcon } from '@synergy/theme-next';
import { Box, IconButton, Tooltip } from '@mui/material';
import { CheckOutlined } from '@mui/icons-material';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { InquiryOfferState } from '@synergy/models';
import { PaloColumn, PaloFilterChips, PaloListView, PaloStatusPill, usePaloListSort } from '@synergy/components-palo';
import { ReactElement, useEffect, useState } from 'react';
import { UrlFilter } from '@synergy/components';
import { getInquiryOfferStateTranslationId } from '@synergy/utils';
import { tokens } from '@synergy/theme-palo';
import { useNavigate, useSearchParams } from 'react-router-dom';
import printJS from 'print-js';
import urljoin from 'url-join';

import * as InquiryActions from '../actions/inquiry-actions';
import { Inquiry } from '../../../common/models/inquiry';
import { PaloInquiriesOverviewFilter } from './palo-inquiries-overview-filter';
import { useSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

/**
 * The state the supplier is in on this inquiry.
 *
 * The legacy table reached straight into accountPurchases[0].offers[0], which
 * throws the moment an inquiry arrives without either — so this asks first.
 */
const offerState = (inquiry: Inquiry): InquiryOfferState | undefined =>
    inquiry.accountPurchases?.[0]?.offers?.[0]?.inquiryOfferState;

export const PaloInquiriesOverview = (): ReactElement => {
    const dispatch = useThunkDispatch();
    const navigate = useNavigate();
    const intl = useIntl();
    const [searchParams, setSearchParams] = useSearchParams();
    // The list opens in its own order, and asks the server for it: the header
    // showed "Nummer" with an arrow while the request carried no order at all,
    // so the oldest document stood on top under a header saying the newest
    // (LST-S16, #2234).
    const { sort, params: sortedParams } = usePaloListSort('inquiries');
    const { data, requesting } = useSelector(state => state.inquiryData);
    const [, setLoadingPdf] = useState(false);

    const page = Number(searchParams.get('page') ?? 1); // 1-based
    const limit = Number(searchParams.get('limit') ?? 50);

    useEffect(() => {
        dispatch(InquiryActions.fetchInquiries(sortedParams));

        return () => {
            dispatch(InquiryActions.resetInquiries());
        };
    }, [dispatch, sortedParams]);

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const print = (id: number): void => {
        const url = urljoin(API_BASE_URL, `inquiries/${id}/pdf-offers`);

        printJS({
            printable: url,
            onLoadingStart: () => setLoadingPdf(true),
            onLoadingEnd: () => setLoadingPdf(false),
            onError: () => {
                // Not every browser can print for us; then just show the file.
                setLoadingPdf(false);
                window.open(url);
            },
        });
    };

    const columns: PaloColumn<Inquiry>[] = [
        {
            key: 'number',
            type: 'documentNumber',
            head: <FormattedMessage id="app.table.column.number" />,
            sortKey: 'number',
            cell: inquiry => inquiry.number,
        },
        {
            key: 'status',
            type: 'status',
            head: <FormattedMessage id="app.table.column.status" />,
            cell: inquiry => {
                const state = offerState(inquiry);

                return state ? (
                    <PaloStatusPill
                        status={state}
                        label={<FormattedMessage id={getInquiryOfferStateTranslationId(state)} />}
                    />
                ) : null;
            },
        },
        {
            key: 'auction',
            type: 'flag',
            head: <FormattedMessage id="app.table.column.auction" />,
            cell: inquiry => inquiry.isAuction && <AuctionIcon sx={{ fontSize: 15 }} />,
        },
        {
            key: 'title',
            type: 'name',
            head: <FormattedMessage id="app.table.column.title" />,
            sortKey: 'title',
            cell: inquiry => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                    {inquiry.hasTransitions && (
                        <CheckOutlined sx={{ fontSize: 15, color: tokens.color.ok, flexShrink: 0 }} />
                    )}
                    <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {inquiry.title}
                    </Box>
                </Box>
            ),
        },
        {
            key: 'customerCount',
            type: 'count',
            head: <FormattedMessage id="app.table.column.customerCount" />,
            cell: inquiry => inquiry.accountPurchases?.length ?? 0,
        },
        {
            key: 'createdAt',
            type: 'date',
            head: <FormattedMessage id="app.table.column.publishDate" />,
            sortKey: 'createdAt',
            cell: inquiry => <FormattedDate value={inquiry.createdAt} year="numeric" month="short" day="2-digit" />,
        },
        {
            key: 'endDate',
            type: 'date',
            head: <FormattedMessage id="app.table.column.endDate" />,
            sortKey: 'endDate',
            cell: inquiry => <FormattedDate value={inquiry.endDate} year="numeric" month="short" day="2-digit" />,
        },
        {
            key: 'print',
            type: 'icon',
            head: '',
            cell: inquiry => (
                <Tooltip title={<FormattedMessage id="app.inquiries.print" />} placement="top">
                    <IconButton
                        size="small"
                        onClick={event => {
                            // The row opens the inquiry; this only prints.
                            event.stopPropagation();
                            print(inquiry.id);
                        }}
                    >
                        <PrintIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    return (
        <UrlFilter useLegacyFilter={true}>
            <PaloListView<Inquiry>
                title={<FormattedMessage id="app.inquiries.overview" />}
                filters={<PaloInquiriesOverviewFilter />}
                filterChips={<PaloFilterChips />}
                sort={sort}
                onSortChange={(key, order) => updateParams({ sort: key, order, page: '1' })}
                searchValue={searchParams.get('search') ?? ''}
                searchPlaceholder={intl.formatMessage({ id: 'app.common.searchPlaceholder' })}
                onSearchChange={value => {
                    const next = new URLSearchParams(searchParams);
                    if (value) {
                        next.set('search', value);
                    } else {
                        next.delete('search');
                    }
                    next.set('page', '1');
                    setSearchParams(next, { replace: true });
                }}
                columns={columns}
                items={data.items}
                getRowId={inquiry => inquiry.id}
                requesting={requesting}
                emptyLabel={<FormattedMessage id="app.common.emptyResult" />}
                onRowClick={inquiry => navigate('/inquiries/' + inquiry.id)}
                pagination={{
                    count: data.total,
                    page,
                    limit,
                    onPageChange: newPage => updateParams({ page: String(newPage) }),
                    onLimitChange: newLimit => updateParams({ limit: String(newLimit), page: '1' }),
                }}
            />
        </UrlFilter>
    );
};
